import type React from 'react'
import { isNumber } from '@mote/helpers/assertion'
import styled, { css } from '@xstyled/styled-components'

function evaluateValue(value?: number | string | null) {
  return isNumber(value) ? `${value}px` : value
}

export interface BlockOptions {
  layout?: 'horizontal' | 'vertical' | 'horizontal-reverse' | 'vertical-reverse'
  justify?:
    | 'initial'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'flex-start'
    | 'flex-end'
  align?: 'flex-start' | 'center' | 'flex-end' | 'stretch'
  gap?: number | string
  grow?: boolean
  spreadX?: boolean
  spreadY?: boolean
  wrapped?: boolean
  zeroMinWidth?: boolean
  zeroMinHeight?: boolean
  width?: number | string
  height?: number | string
  minWidth?: number | string
  maxWidth?: number | string
  minHeight?: number | string
  maxHeight?: number | string
  margin?: number | string
  padding?: number | string
}

export interface BlockProps
  extends React.HTMLAttributes<HTMLDivElement>,
    BlockOptions {}

export const Block = styled.div<BlockProps>`
  display: flex;
  flex-direction: ${({ layout }) => {
    switch (layout) {
      case 'horizontal':
        return 'row'
      case 'horizontal-reverse':
        return 'row-reverse'
      case 'vertical-reverse':
        return 'column-reverse'
      case 'vertical':
      default:
        return 'column'
    }
  }};
  justify-content: ${({ justify }) => (justify ? justify : 'initial')};
  align-items: ${({ align }) => (align ? align : 'initial')};

  ${({ gap }) =>
    gap &&
    css`
      gap: ${evaluateValue(gap)};
    `};

  ${({ zeroMinWidth }) =>
    zeroMinWidth &&
    css`
      min-width: 0;
    `};

  ${({ zeroMinHeight }) =>
    zeroMinHeight &&
    css`
      min-height: 0;
    `};

  ${({ wrapped }) =>
    wrapped &&
    css`
      flex-wrap: wrap;
    `};

  ${({ spreadX }) =>
    spreadX &&
    css`
      width: 100%;
    `};

  ${({ spreadY }) =>
    spreadY &&
    css`
      height: 100%;
    `};

  ${({ grow }) =>
    grow &&
    css`
      flex: 0 0 auto;
    `};

  ${({ width, spreadX }) =>
    !spreadX &&
    width &&
    css`
      width: ${evaluateValue(width)};
    `};

  ${({ height, spreadY }) =>
    !spreadY &&
    height &&
    css`
      height: ${evaluateValue(height)};
    `};

  ${({ minWidth, zeroMinWidth }) =>
    !zeroMinWidth &&
    minWidth &&
    css`
      min-width: ${evaluateValue(minWidth)};
    `};

  ${({ maxWidth }) =>
    maxWidth &&
    css`
      max-width: ${evaluateValue(maxWidth)};
    `};

  ${({ minHeight, zeroMinHeight }) =>
    !zeroMinHeight &&
    minHeight &&
    css`
      min-height: ${evaluateValue(minHeight)};
    `};

  ${({ maxHeight }) =>
    maxHeight &&
    css`
      max-height: ${evaluateValue(maxHeight)};
    `};

  ${({ margin }) =>
    margin &&
    css`
      margin: ${evaluateValue(margin)};
    `};

  ${({ padding }) =>
    padding &&
    css`
      padding: ${evaluateValue(padding)};
    `};
`

export const BlockGroup = styled(Block)``
